import React, { useState } from 'react';
import { useUpdate, useNotify, useRefresh, useRecordContext } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';

interface Props {
  open: boolean;
  setOpen: (open: boolean) => void;
}

const EditEventInviteModal: React.FC<Props> = ({ open, setOpen }) => {
  const record = useRecordContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [update, { isLoading }] = useUpdate();

  type AttendanceMode = 'IN_PERSON' | 'ZOOM' | 'NO_SHOW';

  // null allowed in state
  const [registered, setRegistered] = useState<boolean | null>(record?.registered ?? null);
  const [attended, setAttended] = useState<boolean | null>(record?.attended ?? null);
  const [attendanceMode, setAttendanceMode] = useState<AttendanceMode | null>(
    (record?.attendanceMode as AttendanceMode | null | undefined) ?? null
  );

  if (!record) return null;

  const handleSave = () => {
    update(
      'group-event-invites',
      {
        id: record.id,
        data: {
          registered,
          attended,
          attendanceMode: attended === true ? attendanceMode : null
        }
      },
      {
        onSuccess: () => {
          notify('Invite updated', { type: 'success' });
          refresh();
          setOpen(false);
        },
        onError: () => notify('Error updating invite', { type: 'error' })
      }
    );
  };

  // Helpers to convert null <-> empty string for MUI Select
  const normalize = (val: boolean | null) => (val === null ? '' : val);
  const denormalize = (val: string | boolean) => (val === '' ? null : val === 'true');

  return (
    <>
      <IconButton size="small" onClick={() => setOpen(true)}>
        <EditIcon fontSize="small" />
      </IconButton>

      <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Edit Invite</DialogTitle>

        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Registered</InputLabel>
            <Select
              label="Registered"
              value={normalize(registered)}
              onChange={(e) => setRegistered(denormalize(e.target.value))}
            >
              <MenuItem value="">Unset</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal">
            <InputLabel>Attended</InputLabel>
            <Select
              label="Attended"
              value={normalize(attended)}
              onChange={(e) => {
                const v = denormalize(e.target.value);
                setAttended(v);
                if (v !== true) setAttendanceMode(null);
              }}
            >
              <MenuItem value="">Unset</MenuItem>
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth margin="normal" disabled={attended !== true}>
            <InputLabel>How They Attended</InputLabel>
            <Select
              label="How They Attended"
              value={attendanceMode ?? ''}
              onChange={(e) =>
                setAttendanceMode(
                  (e.target.value as AttendanceMode | '') || null
                )
              }
            >
              <MenuItem value="">Unset</MenuItem>
              <MenuItem value="IN_PERSON">In Person</MenuItem>
              <MenuItem value="ZOOM">Zoom</MenuItem>
              <MenuItem value="NO_SHOW">Registered, No-Show</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setOpen(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={isLoading}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditEventInviteModal;
